import type {
  TargetareSectorMapare,
  TargetareServiciuMapare,
} from "@/types/targetare";
import { ETICHETE_AMBIGUITATE, ETICHETE_REGULA } from "@/utils/targetareConfig";
import {
  raportImplicit,
  type DefinitieRaport,
  type FiltruRapidDef,
} from "@/utils/targetareRaport";
import type { ColoanaTabel } from "@/utils/targetareTabel";

export const ETICHETE_TIP_COD: Record<string, string> = {
  principal: "Cod principal",
  secundar: "Cod secundar",
};

export const AMBIGUITATE_MOSTENITA = "(moștenit)";
export const AMBIGUITATI_COD = ["Neambiguu", "Ambiguu", "Ierarhic"];

export const ETICHETE_AMBIGUITATE_COD: Record<string, string> = {
  ...ETICHETE_AMBIGUITATE,
  [AMBIGUITATE_MOSTENITA]: "Moștenită de la serviciu",
};

export const FARA_SERVICII = "(fără servicii)";

export type RandMapare = {
  cheie: string;
  entity_id: string;
  serviciu: string;
  sector: string;
  sector_id: string;
  anexa: string;
  versiune: number;
  versiune_text: string;
  cod: string;
  descriere: string;
  tip: "principal" | "secundar";
  valid: boolean;
  activ: boolean;
  ambiguitate_cod: string;
  ambiguitate_serviciu: string;
  ambiguitate_efectiva: string;
  explicatie: string;
  corespondenta: string;
  firme_principal: number;
  firme_principal_scop: number;
  firme_autorizat: number;
  firme_autorizat_scop: number;
  firme_termene: number;
  alte_servicii: string[];
  regula: string;
};

function randSectorFaraServicii(s: TargetareSectorMapare): RandMapare {
  return {
    cheie: `sector-${s.sector_id}`,
    entity_id: "",
    serviciu: FARA_SERVICII,
    sector: s.nume,
    sector_id: s.sector_id,
    anexa: s.anexa ?? "",
    versiune: 0,
    versiune_text: "",
    cod: "",
    descriere: "",
    tip: "principal",
    valid: true,
    activ: false,
    ambiguitate_cod: AMBIGUITATE_MOSTENITA,
    ambiguitate_serviciu: "(fără)",
    ambiguitate_efectiva: "(fără)",
    explicatie:
      "Sectorul nu are servicii în nomenclatorul NIS2 al platformei; firmele lui se identifică doar prin liste sau manual.",
    corespondenta: "",
    firme_principal: 0,
    firme_principal_scop: 0,
    firme_autorizat: 0,
    firme_autorizat_scop: 0,
    firme_termene: 0,
    alte_servicii: [],
    regula: "",
  };
}

export function randuriMapare(
  servicii: TargetareServiciuMapare[],
  sectoare: TargetareSectorMapare[] = []
): RandMapare[] {
  const randuri = servicii.flatMap((s) =>
    s.coduri.map((c) => ({
      cheie: `${s.entity_id}-${c.versiune}-${c.secundar ? "s" : "p"}-${c.cod}`,
      entity_id: s.entity_id,
      serviciu: s.name,
      sector: s.sector ?? "(fără sector)",
      sector_id: s.sector_id ?? "",
      anexa: s.anexa ?? "",
      versiune: c.versiune,
      versiune_text: `Rev. ${c.versiune}`,
      cod: c.cod,
      descriere: c.descriere ?? "(cod inexistent în nomenclator)",
      tip: c.secundar ? ("secundar" as const) : ("principal" as const),
      valid: c.valid,
      activ: c.activ,
      ambiguitate_cod: c.ambiguitate ?? AMBIGUITATE_MOSTENITA,
      ambiguitate_serviciu: s.ambiguitate ?? "(fără)",
      ambiguitate_efectiva: c.ambiguitate ?? s.ambiguitate ?? "(fără)",
      explicatie: c.explicatie ?? "",
      corespondenta: c.corespondenta ?? "",
      firme_principal: c.firme_principal,
      firme_principal_scop: c.firme_principal_scop,
      firme_autorizat: c.firme_autorizat,
      firme_autorizat_scop: c.firme_autorizat_scop,
      firme_termene: c.firme_termene,
      alte_servicii: c.alte_servicii,
      regula: s.regula_tip ?? "",
    }))
  );
  const faraServicii = sectoare
    .filter((s) => s.servicii === 0)
    .map(randSectorFaraServicii);
  return [...randuri, ...faraServicii];
}

export const COLOANE_MAPARE: ColoanaTabel<RandMapare>[] = [
  {
    id: "sector",
    eticheta: "Sector",
    tip: "lista",
    latime: 150,
    implicit: true,
  },
  {
    id: "serviciu",
    eticheta: "Serviciu NIS2",
    tip: "text",
    latime: 240,
    implicit: true,
  },
  {
    id: "entity_id",
    eticheta: "Id serviciu",
    tip: "text",
    latime: 90,
    implicit: false,
  },
  {
    id: "versiune_text",
    eticheta: "Versiune",
    tip: "lista",
    latime: 80,
    implicit: true,
    optiuni: ["Rev. 2", "Rev. 3"],
  },
  { id: "cod", eticheta: "CAEN", tip: "text", latime: 80, implicit: true },
  {
    id: "descriere",
    eticheta: "Descriere CAEN",
    tip: "text",
    latime: 240,
    implicit: true,
  },
  {
    id: "tip",
    eticheta: "Tip cod",
    tip: "lista",
    latime: 100,
    implicit: true,
    optiuni: ["principal", "secundar"],
    etichete: ETICHETE_TIP_COD,
  },
  {
    id: "activ",
    eticheta: "Activ",
    tip: "bool",
    latime: 70,
    implicit: true,
  },
  {
    id: "ambiguitate_cod",
    eticheta: "Ambiguitate pe cod",
    tip: "lista",
    latime: 180,
    implicit: true,
    optiuni: [AMBIGUITATE_MOSTENITA, ...AMBIGUITATI_COD],
    etichete: ETICHETE_AMBIGUITATE_COD,
  },
  {
    id: "ambiguitate_efectiva",
    eticheta: "Ambiguitate efectivă",
    tip: "lista",
    latime: 120,
    implicit: false,
    etichete: ETICHETE_AMBIGUITATE,
  },
  {
    id: "explicatie",
    eticheta: "De ce se încadrează",
    tip: "text",
    latime: 320,
    implicit: true,
  },
  {
    id: "corespondenta",
    eticheta: "Corespondență Rev. 2 → 3",
    tip: "lista",
    latime: 180,
    implicit: false,
  },
  {
    id: "valid",
    eticheta: "Cod valid",
    tip: "bool",
    latime: 80,
    implicit: false,
  },
  {
    id: "firme_principal",
    eticheta: "Firme cu CAEN principal",
    tip: "numar",
    latime: 110,
    implicit: true,
  },
  {
    id: "firme_principal_scop",
    eticheta: "… mari/mijlocii",
    tip: "numar",
    latime: 100,
    implicit: true,
  },
  {
    id: "firme_autorizat",
    eticheta: "Firme cu cod autorizat",
    tip: "numar",
    latime: 110,
    implicit: true,
  },
  {
    id: "firme_autorizat_scop",
    eticheta: "… mari/mijlocii",
    tip: "numar",
    latime: 100,
    implicit: true,
  },
  {
    id: "firme_termene",
    eticheta: "Firme Termene",
    tip: "numar",
    latime: 100,
    implicit: false,
  },
  {
    id: "alte_servicii",
    eticheta: "Același cod și la",
    tip: "multi",
    latime: 160,
    implicit: false,
  },
  {
    id: "ambiguitate_serviciu",
    eticheta: "Ambiguitate serviciu",
    tip: "lista",
    latime: 120,
    implicit: false,
    etichete: ETICHETE_AMBIGUITATE,
  },
  {
    id: "regula",
    eticheta: "Regulă de încadrare",
    tip: "lista",
    latime: 200,
    implicit: false,
    etichete: ETICHETE_REGULA,
  },
  { id: "anexa", eticheta: "Anexă", tip: "lista", latime: 80, implicit: false },
];

export const FILTRE_RAPIDE_MAPARE: FiltruRapidDef[] = [
  { coloana: "sector", eticheta: "Sector" },
  { coloana: "serviciu", eticheta: "Serviciu" },
  {
    coloana: "versiune_text",
    eticheta: "Versiune CAEN",
    optiuni: ["Rev. 2", "Rev. 3"],
  },
  {
    coloana: "tip",
    eticheta: "Tip cod",
    optiuni: ["principal", "secundar"],
    etichete: ETICHETE_TIP_COD,
  },
  { coloana: "activ", eticheta: "Activ", optiuni: ["da", "nu"] },
  {
    coloana: "ambiguitate_efectiva",
    eticheta: "Ambiguitate",
    etichete: ETICHETE_AMBIGUITATE,
  },
];

const IMPLICITE = COLOANE_MAPARE.filter((c) => c.implicit).map((c) => c.id);

export const definitieRaportMapare: DefinitieRaport<RandMapare> = {
  cheie: "targetare.rapoarte.mapare",
  coloane: COLOANE_MAPARE,
  rapoarteImplicite: [
    raportImplicit("Toate codurile mapate", IMPLICITE, {
      sortare: [{ id: "sector", desc: false }],
      randuriPePagina: 100,
    }),
    raportImplicit("Coduri dezactivate", IMPLICITE, {
      filtre: [
        {
          id: "f-inactiv",
          coloana: "activ",
          operator: "egal",
          valoare: "nu",
          activ: true,
        },
      ],
    }),
    raportImplicit("Ambiguitate setată pe cod", IMPLICITE, {
      filtre: [
        {
          id: "f-amb-cod",
          coloana: "ambiguitate_cod",
          operator: "diferit",
          valoare: AMBIGUITATE_MOSTENITA,
          activ: true,
        },
      ],
    }),
    raportImplicit("Coduri partajate între servicii", IMPLICITE, {
      filtre: [
        {
          id: "f-alte",
          coloana: "alte_servicii",
          operator: "nu_este_gol",
          activ: true,
        },
      ],
    }),
    raportImplicit("Coduri fără firme", IMPLICITE, {
      filtre: [
        {
          id: "f-p",
          coloana: "firme_principal",
          operator: "egal",
          valoare: "0",
          activ: true,
        },
        {
          id: "f-a",
          coloana: "firme_autorizat",
          operator: "egal",
          valoare: "0",
          activ: true,
        },
      ],
    }),
  ],
  cheieRand: (r) => r.cheie,
  filtreRapide: FILTRE_RAPIDE_MAPARE,
  numeFisier: "mapare-caen",
  mesajGol: "Maparea nu a putut fi încărcată.",
};
